import * as React from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "../../../lib/utils";

/**
 * Pagination Component - Drop-in replacement for antd Pagination
 *
 * @example
 * <Pagination
 *   current={currentPage}
 *   total={100}
 *   pageSize={10}
 *   onChange={(page, pageSize) => setCurrentPage(page)}
 *   showSizeChanger
 *   pageSizeOptions={[10, 20, 50]}
 * />
 */
const Pagination = ({
  current = 1,
  total = 0,
  pageSize = 10,
  onChange,
  showSizeChanger = false,
  pageSizeOptions = [10, 20, 50, 100],
  onShowSizeChange,
  className,
  isDarkMode = true, // OLED: always dark
}) => {
  const totalPages = Math.ceil(total / pageSize);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== current) {
      onChange?.(page, pageSize);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    onShowSizeChange?.(1, newSize);
    onChange?.(1, newSize);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5; // Number of page buttons to show

    if (totalPages <= showPages + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and surrounding pages
      pages.push(1);

      let start = Math.max(2, current - 1);
      let end = Math.min(totalPages - 1, current + 1);

      // Adjust range if at the start or end
      if (current <= 3) {
        end = 4;
      } else if (current >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push('ellipsis-start');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('ellipsis-end');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const baseButtonStyles = cn(
    "inline-flex items-center justify-center min-w-[32px] h-8 px-2 text-sm font-medium rounded transition-colors",
    "bg-gray-700 text-white hover:bg-gray-600 border-transparent"
  );

  const activeButtonStyles = cn(
    baseButtonStyles,
    "bg-gray-500 text-white"
  );

  const disabledButtonStyles = cn(
    baseButtonStyles,
    "opacity-50 cursor-not-allowed pointer-events-none"
  );

  if (totalPages === 0) return null;

  return (
    <nav
      className={cn(
        "flex items-center justify-center gap-1 p-2 rounded-lg",
        "bg-gray-800",
        className
      )}
      role="navigation"
      aria-label="Pagination"
    >
      {/* Previous button */}
      <button
        className={current === 1 ? disabledButtonStyles : baseButtonStyles}
        onClick={() => handlePageChange(current - 1)}
        disabled={current === 1}
        aria-label="Go to previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Page numbers */}
      {getPageNumbers().map((page, index) => {
        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
          return (
            <span
              key={page}
              className={cn(
                "inline-flex items-center justify-center min-w-[32px] h-8",
                "text-gray-400"
              )}
            >
              <MoreHorizontal className="h-4 w-4" />
            </span>
          );
        }

        return (
          <button
            key={page}
            className={page === current ? activeButtonStyles : baseButtonStyles}
            onClick={() => handlePageChange(page)}
            aria-label={`Go to page ${page}`}
            aria-current={page === current ? "page" : undefined}
          >
            {page}
          </button>
        );
      })}

      {/* Next button */}
      <button
        className={current === totalPages ? disabledButtonStyles : baseButtonStyles}
        onClick={() => handlePageChange(current + 1)}
        disabled={current === totalPages}
        aria-label="Go to next page"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {/* Page size selector */}
      {showSizeChanger && (
        <select
          value={pageSize}
          onChange={handlePageSizeChange}
          className={cn(
            "ml-2 h-8 px-2 text-sm rounded border transition-colors",
            "bg-gray-700 text-white border-transparent"
          )}
          aria-label="Select page size"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>
      )}
    </nav>
  );
};

export { Pagination };
